import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import type { TimberProject } from '../../../types';
import type { CrewJob, CrewJobPayUnit } from '../../../types/crewJobs';
import { normalizeCrewJobPayBand } from '../../../utils/crewJobsPay';
import { useCRM } from '../../../utils/CRMContext';
import {
  formatCrewTaskOptionLabel,
  resolveCrewJobDates,
  tasksForCrewJobProject,
} from '../../../utils/crewJobsDates';
import {
  createCrewJob,
  parseDateInput,
  toDateInputValue,
  updateCrewJob,
  type CrewJobInput,
} from '../../../utils/crewJobsFirestore';

type DateMode = 'manual' | 'task';

type Props = {
  job: CrewJob | null;
  timberProjects: TimberProject[];
  onClose: () => void;
};

const CrewJobEditor: React.FC<Props> = ({ job, timberProjects, onClose }) => {
  const { projectTasks, requestCrmCollections } = useCRM();
  const isEdit = !!job?.id;
  const [title, setTitle] = useState(job?.title || '');
  const [location, setLocation] = useState(job?.location || '');
  const [locationLat, setLocationLat] = useState<number | null>(job?.locationLat ?? null);
  const [locationLng, setLocationLng] = useState<number | null>(job?.locationLng ?? null);
  const [hotelName, setHotelName] = useState(job?.hotelName || '');
  const [hotelLocation, setHotelLocation] = useState(job?.hotelLocation || '');
  const [hotelLat, setHotelLat] = useState<number | null>(job?.hotelLat ?? null);
  const [hotelLng, setHotelLng] = useState<number | null>(job?.hotelLng ?? null);
  const [description, setDescription] = useState(job?.description || '');
  const [startDate, setStartDate] = useState(toDateInputValue(job?.startDate));
  const [endDate, setEndDate] = useState(toDateInputValue(job?.endDate));
  const [startMode, setStartMode] = useState<DateMode>(job?.startTaskId ? 'task' : 'manual');
  const [endMode, setEndMode] = useState<DateMode>(job?.endTaskId ? 'task' : 'manual');
  const [startTaskId, setStartTaskId] = useState(job?.startTaskId || '');
  const [endTaskId, setEndTaskId] = useState(job?.endTaskId || '');
  const [crewNeeded, setCrewNeeded] = useState(String(job?.crewNeeded || 6));
  const existingPay = normalizeCrewJobPayBand({
    unit: job?.payUnit,
    min: job?.payMin,
    max: job?.payMax,
  });
  const [payUnit, setPayUnit] = useState<CrewJobPayUnit>(existingPay?.unit || 'day');
  const [payMin, setPayMin] = useState(existingPay ? String(existingPay.min) : '');
  const [payMax, setPayMax] = useState(
    existingPay && existingPay.max !== existingPay.min ? String(existingPay.max) : '',
  );
  const [projectId, setProjectId] = useState(job?.projectId || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    requestCrmCollections(['projectTasks']);
  }, [requestCrmCollections]);

  const projectOptions = useMemo(
    () =>
      [...timberProjects]
        .filter(p => p.id)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [timberProjects],
  );

  const selectedProject = projectOptions.find(p => p.id === projectId) || null;
  const projectTasksForJob = useMemo(
    () => tasksForCrewJobProject(projectTasks, selectedProject?.id),
    [projectTasks, selectedProject?.id],
  );

  const startTask = projectTasksForJob.find(t => t.id === startTaskId) || null;
  const endTask = projectTasksForJob.find(t => t.id === endTaskId) || null;

  const resolved = resolveCrewJobDates({
    startDate: parseDateInput(startDate) || job?.startDate || null,
    endDate: parseDateInput(endDate) || job?.endDate || null,
    startTaskId: startMode === 'task' ? startTaskId || null : null,
    endTaskId: endMode === 'task' ? endTaskId || null : null,
  }, projectTasksForJob);

  const payload = useMemo<CrewJobInput>(() => {
    const pay = normalizeCrewJobPayBand({ unit: payUnit, min: payMin, max: payMax });
    return {
      title,
      location,
      locationLat,
      locationLng,
      hotelName,
      hotelLocation,
      hotelLat,
      hotelLng,
      description,
      startDate: resolved.startDate,
      endDate: resolved.endDate,
      startTaskId: startMode === 'task' ? startTaskId || null : null,
      endTaskId: endMode === 'task' ? endTaskId || null : null,
      crewNeeded: Number(crewNeeded) || 1,
      payUnit: pay?.unit ?? null,
      payMin: pay?.min ?? null,
      payMax: pay?.max ?? null,
      projectId: selectedProject?.id || null,
      dealId: selectedProject?.dealId || null,
      projectName: selectedProject?.name || null,
    };
  }, [title, location, locationLat, locationLng, hotelName, hotelLocation, hotelLat, hotelLng, description, resolved, startMode, endMode, startTaskId, endTaskId, crewNeeded, payUnit, payMin, payMax, selectedProject]);

  const applyProject = (nextId: string) => {
    setProjectId(nextId);
    const next = projectOptions.find(p => p.id === nextId);
    if (next?.location && !location.trim()) {
      setLocation(next.location);
      if (next.coordinates) {
        setLocationLat(next.coordinates.lat);
        setLocationLng(next.coordinates.lng);
      }
    }
    if (!nextId) {
      setStartMode('manual');
      setEndMode('manual');
      setStartTaskId('');
      setEndTaskId('');
      return;
    }
    const stillOnProject = (taskId: string) =>
      projectTasks.some(t => t.id === taskId && t.projectId === nextId);
    if (startTaskId && !stillOnProject(startTaskId)) {
      setStartTaskId('');
      setStartMode('manual');
    }
    if (endTaskId && !stillOnProject(endTaskId)) {
      setEndTaskId('');
      setEndMode('manual');
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (startMode === 'task' && !startTaskId) {
      setError('Choose a start task, or switch start date to a picked date.');
      return;
    }
    if (endMode === 'task' && !endTaskId) {
      setError('Choose an end task, or switch end date to a picked date.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isEdit && job) {
        await updateCrewJob(job.id, payload);
      } else {
        await createCrewJob(payload);
      }
      onClose();
    } catch (e) {
      setError((e as Error).message || 'Failed to save crew job');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="people-hiring-modal-backdrop" onClick={onClose}>
      <div className="people-hiring-modal" onClick={e => e.stopPropagation()}>
        <div className="people-hiring-modal-header">
          <h3>{isEdit ? 'Edit crew job' : 'New crew job'}</h3>
          <button type="button" className="pm-btn pm-btn-ghost" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="people-hiring-modal-body">
          {error && <p style={{ color: '#b91c1c', margin: 0 }}>{error}</p>}
          <div className="people-hiring-field">
            <label>Title</label>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Carriage House raise" />
          </div>
          <div className="people-hiring-field">
            <label>Project (optional)</label>
            <select
              value={projectId}
              onChange={e => applyProject(e.target.value)}
            >
              <option value="">None</option>
              {projectOptions.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div className="people-hiring-field">
            <label>Jobsite location</label>
            <input
              value={location}
              onChange={e => {
                setLocation(e.target.value);
                setLocationLat(null);
                setLocationLng(null);
              }}
              placeholder="Chesterfield, VA"
            />
          </div>
          <div className="people-hiring-row">
            <div className="people-hiring-field">
              <label>Hotel (optional)</label>
              <input
                value={hotelName}
                onChange={e => setHotelName(e.target.value)}
                placeholder="Hampton Inn Gettysburg"
              />
            </div>
            <div className="people-hiring-field">
              <label>Hotel address (optional)</label>
              <input
                value={hotelLocation}
                onChange={e => {
                  setHotelLocation(e.target.value);
                  setHotelLat(null);
                  setHotelLng(null);
                }}
                placeholder="Only shown if set"
              />
            </div>
          </div>
          <p className="people-hiring-muted">
            Hotel is omitted from the public posting unless you fill it in.
          </p>
          <div className="people-hiring-row">
            <DateSourceField
              label="Start date"
              mode={startMode}
              onModeChange={mode => {
                if (mode === 'manual' && startTask?.startDate) {
                  setStartDate(toDateInputValue(startTask.startDate));
                }
                setStartMode(mode);
              }}
              manualValue={startDate}
              onManualChange={setStartDate}
              taskId={startTaskId}
              onTaskIdChange={id => {
                setStartTaskId(id);
                if (!endTaskId && endMode === 'task') setEndTaskId(id);
              }}
              tasks={projectTasksForJob}
              which="start"
              projectSelected={!!selectedProject}
            />
            <DateSourceField
              label="End date"
              mode={endMode}
              onModeChange={mode => {
                if (mode === 'manual' && endTask?.endDate) {
                  setEndDate(toDateInputValue(endTask.endDate));
                }
                setEndMode(mode);
              }}
              manualValue={endDate}
              onManualChange={setEndDate}
              taskId={endTaskId}
              onTaskIdChange={id => {
                setEndTaskId(id);
                if (!startTaskId && startMode === 'task') setStartTaskId(id);
              }}
              tasks={projectTasksForJob}
              which="end"
              projectSelected={!!selectedProject}
            />
          </div>
          <div className="people-hiring-field">
            <label>Crew needed</label>
            <input
              type="number"
              min={1}
              value={crewNeeded}
              onChange={e => setCrewNeeded(e.target.value)}
            />
          </div>
          <div className="people-hiring-field">
            <label>Pay band</label>
            <div className="people-hiring-date-modes" role="radiogroup" aria-label="Pay band unit">
              <label className="people-hiring-date-mode">
                <input
                  type="radio"
                  name="crew-pay-unit"
                  checked={payUnit === 'hour'}
                  onChange={() => setPayUnit('hour')}
                />
                Hourly
              </label>
              <label className="people-hiring-date-mode">
                <input
                  type="radio"
                  name="crew-pay-unit"
                  checked={payUnit === 'day'}
                  onChange={() => setPayUnit('day')}
                />
                Day rate
              </label>
            </div>
            <div className="people-hiring-row">
              <input
                type="number"
                min={1}
                step="1"
                inputMode="decimal"
                value={payMin}
                onChange={e => setPayMin(e.target.value)}
                placeholder={payUnit === 'day' ? 'From, e.g. 400' : 'From, e.g. 45'}
                aria-label="Pay from"
              />
              <input
                type="number"
                min={1}
                step="1"
                inputMode="decimal"
                value={payMax}
                onChange={e => setPayMax(e.target.value)}
                placeholder="To (optional)"
                aria-label="Pay to"
              />
            </div>
            <p className="people-hiring-muted">
              Leave both blank to omit pay. Leave To blank for a single rate.
            </p>
          </div>
          <div className="people-hiring-field">
            <label>Notes for contractors</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What they should know: tools, start time, site conditions."
            />
          </div>
          <div className="people-hiring-modal-footer">
            <button type="button" className="pm-btn pm-btn-ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="ops-new-btn" onClick={() => void handleSave()} disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Save draft'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

type DateSourceFieldProps = {
  label: string;
  mode: DateMode;
  onModeChange: (mode: DateMode) => void;
  manualValue: string;
  onManualChange: (value: string) => void;
  taskId: string;
  onTaskIdChange: (id: string) => void;
  tasks: ReturnType<typeof tasksForCrewJobProject>;
  which: 'start' | 'end';
  projectSelected: boolean;
};

const DateSourceField: React.FC<DateSourceFieldProps> = ({
  label,
  mode,
  onModeChange,
  manualValue,
  onManualChange,
  taskId,
  onTaskIdChange,
  tasks,
  which,
  projectSelected,
}) => (
  <div className="people-hiring-field">
    <label>{label}</label>
    <div className="people-hiring-date-modes" role="radiogroup" aria-label={label}>
      <label className="people-hiring-date-mode">
        <input
          type="radio"
          name={`${label}-mode`}
          checked={mode === 'manual'}
          onChange={() => onModeChange('manual')}
        />
        Pick a date
      </label>
      <label className={`people-hiring-date-mode ${!projectSelected ? 'is-disabled' : ''}`}>
        <input
          type="radio"
          name={`${label}-mode`}
          checked={mode === 'task'}
          disabled={!projectSelected}
          onChange={() => onModeChange('task')}
        />
        Link to task
      </label>
    </div>
    {mode === 'manual' ? (
      <input type="date" value={manualValue} onChange={e => onManualChange(e.target.value)} />
    ) : (
      <>
        <select value={taskId} onChange={e => onTaskIdChange(e.target.value)}>
          <option value="">{tasks.length ? 'Choose a task' : 'No tasks on this project'}</option>
          {tasks.map(t => (
            <option key={t.id} value={t.id}>{formatCrewTaskOptionLabel(t, which)}</option>
          ))}
        </select>
        <p className="people-hiring-muted">
          {which === 'start'
            ? 'Uses that task’s start date. End can stay on this task or another.'
            : 'Uses that task’s end date. Can be the same task as start, or a different one.'}
        </p>
      </>
    )}
  </div>
);

export default CrewJobEditor;
